<script setup>
import { userAuthState } from '@/@core/auth'
import { use2FA } from '@/composables/use2FA'
import loginBg from '@images/pages/login-bg.jpg'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import { RouterLink, useRouter } from 'vue-router'

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const router = useRouter()
const { requireVerification } = use2FA()

const form = ref({
  employee_code: '',
  password: '',
  remember: false,
})

const errors = ref({
  employee_code: null,
  password: null,
})

const loading = ref(false)

const loginForm = ref()
const isPasswordVisible = ref(false)

const authThemeMask = loginBg

// submit form
const onSubmit = () => {
  loginForm.value?.validate().then(({ valid: isValid }) => {
    if (isValid)
      loading.value = true
    login()
  })
}

const login = async () => {
  try {
    const res = await userAuthState().login(form.value)

    // redirect to home
    if (!res.status) {
      // check if the error is a string or an object
      errors.value = res.errors
    } else if (res.requires2FA) {
      // Set up 2FA verification state
      requireVerification(res.twoFactorMethod, res.maskedTarget)

      // Redirect to 2FA verification page
      router.push('/app/two-step')
    } else {
      await userAuthState().fetchUser()
      router.push('/app/panel')
    }
  } catch (error) {
    console.error('login error', error)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <VRow
    no-gutters
    class="auth-wrapper bg-surface"
  >
    <VCol
      md="8"
      class="d-none d-md-flex"
    >
      <div class="position-relative bg-background rounded-lg w-100 ma-8 me-0">
        <VImg
          class="auth-footer-mask"
          :src="authThemeMask"
        />
      </div>
    </VCol>

    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-4"
      >
        <VCardText>
          <VNodeRenderer
            :nodes="themeConfig.app.client.logo"
            class="mb-6"
          />
          <!--
            <h4 class="text-h4 mb-1">
            <span class="text-capitalize ">{{ themeConfig.app.title }}</span>
            </h4> 
          -->
        </VCardText>
        <VCardText>
          <VForm
            ref="loginForm"
            @submit.prevent="onSubmit"
          >
            <VRow>
              <!-- email -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.employee_code"
                  autofocus
                  label="Employee Code"
                  type="text"
                  placeholder="Enter your employee code"
                  :rules="[requiredValidator]"
                  :error-messages="errors.employee_code"
                />
              </VCol>

              <!-- password -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.password"
                  label="Password"
                  placeholder="············"
                  :rules="[requiredValidator]"
                  :error-messages="errors.password"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />
                <div class="d-flex align-center flex-wrap justify-space-between mt-2 mb-4">
                  <VCheckbox
                    v-model="form.remember"
                    label="Remember me"
                  />
                  <RouterLink
                    to="/forgot-password"
                    target="_self"
                    class="text-primary ms-2 mb-1"
                  >
                    Forgot Password?
                  </RouterLink>
                </div>

                <VBtn
                  type="submit"
                  block
                  :loading="loading"
                >
                  Login
                </VBtn>

                <VBtn
                  class="mt-2"
                  variant="tonal"
                  block
                  :disabled="loading"
                  @click="router.push({ name: 'care-visit-kiosk-registration' })"
                >
                  Login As Kiosk
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
        <VCardActions class=" justify-between">
          <span class="text-overline me-3">
            Powered by
          </span>
          <VSpacer />
          <a
            href="https://wecarexm.com"
            target="_blank"
            rel="noopener noreferrer"
            class="text-primary ms-1"
          >
            <VNodeRenderer
              :nodes="themeConfig.app.bigLogo"
              class="mb-6"
            />
          </a>
        </VCardActions>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";
</style>
